import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/assets";

const empty = { date: "", start_time: "", end_time: "", location_name: "", address: "", notes: "", map_x: 50, map_y: 50 };

export default function ScheduleManager() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/admin/schedules").then((res) => setEntries(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm({ ...empty, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ date: e.date, start_time: e.start_time, end_time: e.end_time, location_name: e.location_name, address: e.address, notes: e.notes, map_x: e.map_x, map_y: e.map_y }); setOpen(true); };

  const save = async () => {
    if (!form.date || !form.location_name) { toast.error("Date and location are required"); return; }
    try {
      if (editing) await api.put(`/admin/schedules/${editing.id}`, form);
      else await api.post("/admin/schedules", form);
      toast.success("Schedule saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this stop?")) return;
    await api.delete(`/admin/schedules/${id}`);
    toast.success("Removed");
    load();
  };

  const pickOnMap = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 100);
    const y = Math.round(((e.clientY - r.top) / r.height) * 100);
    setForm((f) => ({ ...f, map_x: x, map_y: y }));
  };

  return (
    <div data-testid="admin-schedule">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-3xl text-sc-espresso">Cart Schedule</h2>
          <p className="text-sm text-sc-espresso/60 mt-1">Set the dates, times & map location for each stop.</p>
        </div>
        <button onClick={openAdd} data-testid="add-schedule-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sc-espresso text-sc-cream text-sm font-medium hover:bg-sc-gold transition-colors">
          <Plus size={18} /> Add Stop
        </button>
      </div>

      <div className="space-y-3">
        {entries.length === 0 && <p className="text-sc-espresso/50">No stops scheduled yet.</p>}
        {entries.map((e) => (
          <div key={e.id} className="bg-white rounded-2xl p-5 border border-sc-gold/15 flex items-center justify-between gap-4" data-testid={`schedule-row-${e.id}`}>
            <div className="flex items-start gap-4">
              <div className="text-center bg-sc-blue/40 rounded-xl px-3 py-2 min-w-[64px]">
                <div className="eyebrow text-[0.6rem]">{new Date(e.date + "T00:00").toLocaleDateString("en-US", { month: "short" })}</div>
                <div className="font-serif-display text-2xl text-sc-espresso leading-none">{new Date(e.date + "T00:00").getDate()}</div>
              </div>
              <div>
                <h3 className="font-serif-display text-xl text-sc-espresso flex items-center gap-2"><MapPin size={16} className="text-sc-sage" />{e.location_name}</h3>
                <p className="text-sm text-sc-espresso/60">{e.start_time} {e.end_time && `– ${e.end_time}`} {e.address && `· ${e.address}`}</p>
                {e.notes && <p className="text-sm text-sc-espresso/50 italic mt-0.5">{e.notes}</p>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => openEdit(e)} className="p-2 rounded-lg hover:bg-sc-blue/40 text-sc-espresso/70"><Pencil size={16} /></button>
              <button onClick={() => remove(e.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-sc-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing ? "Edit Stop" : "Add Stop"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <Field label="Date"><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="schedule-date-input" className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start time"><input value={form.start_time} placeholder="11:00 AM" onChange={(e) => setForm({ ...form, start_time: e.target.value })} className={inputCls} /></Field>
              <Field label="End time"><input value={form.end_time} placeholder="5:00 PM" onChange={(e) => setForm({ ...form, end_time: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Location name"><input value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} data-testid="schedule-location-input" className={inputCls} /></Field>
            <Field label="Address"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></Field>
            <Field label="Special notes"><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " resize-none"} /></Field>
            <div>
              <label className="eyebrow block mb-1.5">Map position — click to place the cart</label>
              <div className="relative rounded-xl overflow-hidden border border-sc-gold/25 cursor-crosshair max-w-[260px] mx-auto" onClick={pickOnMap} data-testid="schedule-map-picker">
                <img src={ASSETS.mangaMap} alt="map" className="w-full" style={{ aspectRatio: "848/1264", objectFit: "cover" }} draggable={false} />
                <span className="absolute -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-sc-sage border-2 border-white shadow" style={{ left: `${form.map_x}%`, top: `${form.map_y}%` }} />
              </div>
              <p className="text-xs text-sc-espresso/50 mt-1">x: {form.map_x}% · y: {form.map_y}%</p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-schedule-btn" className="px-6 py-2.5 rounded-full bg-sc-espresso text-sc-cream font-medium hover:bg-sc-gold transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full bg-white border border-sc-gold/25 rounded-xl px-4 py-2.5 text-sc-espresso focus:outline-none focus:border-sc-gold";
function Field({ label, children }) {
  return (<div><label className="eyebrow block mb-1.5">{label}</label>{children}</div>);
}
